/**
 * The answer-grounding RECOVERY request (MAR-206): what the badge's "Revise answer" and "Find
 * supporting sources" actions send, instead of a prompt.
 *
 * The browser used to compose the revision instruction itself and send it as a hidden user message.
 * The prompt now lives in the agent service that sends it (services/agent-web-chat/AGENTS.md):
 * `services/agent-web-chat/src/marcus_web_chat/recovery.py`. The browser sends this structured request
 * plus `GROUNDING_RECOVERY_SENTINEL` as the turn's text; `chat.post.ts` validates the request and
 * forwards it on the A2A `params.metadata` under `GROUNDING_RECOVERY_METADATA_KEY`; the coordinator
 * swaps the sentinel for its own instruction in the USER turn.
 *
 * CLIENT-SUPPLIED, SO BOUNDED. `previousAnswer` and `flaggedClaims` come from the browser. They only
 * ever reach the user turn, never a system instruction, but they still become model input, so this
 * parser caps their size before they travel. The Python side applies the same caps again.
 *
 * `tests/unit/groundingRecovery.test.ts` reads `recovery.py` and fails if either constant drifts.
 */

export const GROUNDING_RECOVERY_ACTIONS = ['revise', 'find_supporting'] as const
export type GroundingRecoveryAction = typeof GROUNDING_RECOVERY_ACTIONS[number]

/** The user-turn text a recovery request carries in place of an instruction. */
export const GROUNDING_RECOVERY_SENTINEL = '[marcus:answer-grounding-recovery]'

/** The A2A `params.metadata` key the coordinator reads the request from. */
export const GROUNDING_RECOVERY_METADATA_KEY = 'web_chat_recovery'

const MAX_ANSWER_CHARS = 20_000
const MAX_CLAIMS = 20
const MAX_FIELD_CHARS = 1_000

export interface GroundingRecoveryClaim {
  claim: string
  verdict: string
  reason: string
  severity?: string
  citationLabels?: string[]
}

export interface GroundingRecoveryRequest {
  action: GroundingRecoveryAction
  /** The assistant message being recovered. Identity only; the text travels in `previousAnswer`. */
  messageId: string
  previousAnswer: string
  flaggedClaims: GroundingRecoveryClaim[]
}

function clip(value: unknown, limit = MAX_FIELD_CHARS): string {
  return typeof value === 'string' ? value.trim().slice(0, limit) : ''
}

function parseClaim(value: unknown): GroundingRecoveryClaim | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const claim = clip(raw.claim)
  if (!claim) return null
  const out: GroundingRecoveryClaim = { claim, verdict: clip(raw.verdict, 40), reason: clip(raw.reason) }
  if (typeof raw.severity === 'string') out.severity = clip(raw.severity, 20)
  if (Array.isArray(raw.citationLabels)) {
    out.citationLabels = raw.citationLabels.filter((l): l is string => typeof l === 'string').slice(0, 10).map(l => clip(l, 200))
  }
  return out
}

/** A validated, bounded request, or `null` for anything that is not one. */
export function parseGroundingRecovery(value: unknown): GroundingRecoveryRequest | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const action = raw.action
  if (typeof action !== 'string' || !(GROUNDING_RECOVERY_ACTIONS as readonly string[]).includes(action)) return null
  const messageId = clip(raw.messageId, 100)
  const previousAnswer = clip(raw.previousAnswer, MAX_ANSWER_CHARS)
  const flaggedClaims = Array.isArray(raw.flaggedClaims)
    ? raw.flaggedClaims.map(parseClaim).filter((c): c is GroundingRecoveryClaim => c !== null).slice(0, MAX_CLAIMS)
    : []
  if (!messageId || !previousAnswer || !flaggedClaims.length) return null
  return { action: action as GroundingRecoveryAction, messageId, previousAnswer, flaggedClaims }
}

/** `POST /api/chat/revalidate`: the answer to re-check, as the user sees it. */
export interface GroundingRevalidateRequest {
  answerText: string
  /**
   * The assistant message being re-checked (MAR-1390). Required for a `free_tier` member (MAR-1452,
   * D19), whose re-check is judged on the answer the server STORED for their own message — never on
   * `answerText` — so a free allowance cannot be spent judging arbitrary text. For a paid member it
   * only picks the conversation's jurisdiction; their `answerText` is judged.
   */
  messageId?: string
}

/**
 * The response. `result` is the same payload a `validation_result` wire event carries
 * (`shared/chat/wireEvents.ts`), so the client normalizes both with `normalizeValidationResult`.
 */
export interface GroundingRevalidateResponse {
  result: unknown
}

/** The longest answer `/api/chat/revalidate` will judge, the same cap a recovery request applies. */
export const GROUNDING_REVALIDATE_MAX_CHARS = MAX_ANSWER_CHARS
